import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import PaginationControls from './PaginationControls';

const AssignTraining = () => {
  const { id: trainingId } = useParams();
  const [training, setTraining] = useState(null); // State to hold training details
  const [participants, setParticipants] = useState(null);
  const [registeredUserIds, setRegisteredUserIds] = useState(new Set()); // Track registered user IDs
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedKIFlags, setSelectedKIFlags] = useState({});
  const [selectedEmployeeNames, setSelectedEmployeeNames] = useState({});
  const [error, setError] = useState('');
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeTotalPages, setEmployeeTotalPages] = useState(1);
  const [employeeTotalRows, setEmployeeTotalRows] = useState(0);
  const [qualificationStatus, setQualificationStatus] = useState({ qualifications: [], users: {} });

  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetch(`${API_BASE_URL}/trainings/get_training.php?id=${trainingId}`,{
        credentials: 'include'  // This ensures cookies (including session cookies) are sent
      })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        //if (data.success) {
          setTraining(data); // Set training details in state
        //}
      })
      .catch(error => console.error('Error fetching training:', error));
  }, [trainingId]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/trainings/get_training_participants.php?id=${trainingId}`,{
        credentials: 'include'  // This ensures cookies (including session cookies) are sent
      })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        // Handle both array and empty array responses
        if (Array.isArray(data)) {
          setParticipants(data);
          const userIds = new Set(data.map(p => p.user_id));
          setRegisteredUserIds(userIds);
        } else {
          // Handle case where backend returns empty array
          setParticipants([]);
          setRegisteredUserIds(new Set());
        }
      })
      .catch(error => console.error('Error fetching training:', error));
  }, [trainingId]);
 
 
  useEffect(() => {
    // Fetch employees under the admin's control, paginated. selectedEmployees
    // tracks IDs independently of what's currently rendered, so checking a
    // box on one page and navigating to another preserves the selection.
    fetch(`${API_BASE_URL}/users/get_users.php?page=${employeePage}&status=active`,{
        credentials: 'include'  // This ensures cookies (including session cookies) are sent
      })
      .then(response => response.json())
      .then(data => {
        setEmployees(data.items || []);
        setEmployeeTotalPages(data.total_pages || 1);
        setEmployeeTotalRows(data.total_rows || 0);
      })
      .catch(err => setError('Failed to fetch employees'));
  }, [employeePage]);

  useEffect(() => {
    // Non-blocking, informational-only — fetched only for the currently
    // rendered page of employees, not the whole scoped list.
    if (employees.length === 0) {
      setQualificationStatus({ qualifications: [], users: {} });
      return;
    }
    const params = new URLSearchParams({ training_id: trainingId });
    employees.forEach(e => params.append('user_ids[]', e.id));
    fetch(`${API_BASE_URL}/qualifications/get_prerequisite_qualification_status.php?${params.toString()}`, {
      credentials: 'include'
    })
      .then(response => response.json())
      .then(data => setQualificationStatus({ qualifications: data.qualifications || [], users: data.users || {} }))
      .catch(err => console.error('Error fetching qualification status:', err));
  }, [trainingId, employees]);

  // Worst-case icon per employee: red if any required qualification is
  // missing, amber if none missing but at least one expired, green if every
  // one is currently held (or the course requires none at all).
  const qualificationBadge = (userId) => {
    if (qualificationStatus.qualifications.length === 0) {
      return null;
    }
    const statuses = qualificationStatus.users[userId] || {};
    const values = qualificationStatus.qualifications.map(q => statuses[q.id]);
    const worst = values.includes('missing') ? 'missing' : values.includes('expired') ? 'expired' : 'held';
    const tooltip = qualificationStatus.qualifications
      .map(q => `${q.name}: ${statuses[q.id] || 'missing'}`)
      .join('\n');
    if (worst === 'missing') {
      return <i className="bi bi-x-circle-fill" style={{ color: 'red' }} title={tooltip}></i>;
    }
    if (worst === 'expired') {
      return <i className="bi bi-exclamation-triangle-fill" style={{ color: '#e6a700' }} title={tooltip}></i>;
    }
    return <i className="bi bi-check-circle-fill" style={{ color: 'green' }} title={tooltip}></i>;
  };

  const handleCheckboxChangeX = (userId) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId); // Remove from selection
      } else {
        return [...prev, userId]; // Add to selection
      }
    });
  };
  const handleCheckboxChange = (userId) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(userId)) {
        const newSelected = prev.filter(id => id !== userId);
        const newKIFlags = { ...selectedKIFlags };
        delete newKIFlags[userId];
        setSelectedKIFlags(newKIFlags);
        return newSelected;
      } else {
        return [...prev, userId];
      }
    });
    // Remember the name at selection time, since the employee may not be on
    // the currently-loaded page anymore by the time we need it (e.g. for the
    // missing-prerequisites message after selecting across multiple pages).
    const employee = employees.find(e => e.id === userId);
    if (employee) {
      setSelectedEmployeeNames(prev => ({ ...prev, [userId]: employee.name }));
    }
  };
  const handleKICheckboxChange = (userId) => {
    setSelectedKIFlags((prev) => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };
  const handleAssign = (bypassPrerequisites = false) => {
    if (selectedEmployees.length === 0) {
      Swal.fire({ icon: 'warning', text: 'Please select at least one employee.' });
      return;
    }

    fetch(`${API_BASE_URL}/trainings/assign_training.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
        training_id: trainingId,
        employees: selectedEmployees.map(id => ({
          id,
          is_KI: !!selectedKIFlags[id]
        })),
        bypassPrerequisites,
      }),
      credentials: 'include', // Include cookies for session
    })
    .then(response => {
        const contentType = response.headers.get('content-type');
    
        // Log the status and response text for debugging
        console.log('Response Status:', response.status);
        
        // Attempt to read the response as text first
        return response.text().then((text) => {
          console.log('Raw Response:', text); // Log the raw response text
          
          if (!response.ok) {
            // If response is not ok, throw an error with the response text
            throw new Error(`Failed with status ${response.status}: ${text}`);
          }
    
          // If response is JSON, parse it
          if (contentType && contentType.includes('application/json')) {
            return JSON.parse(text); // Parse JSON manually
          } else {
            // Return the raw text if it's not JSON
            return text;
          }
        });
      })
      .then(data => {
        console.log("data",data);
        // Check if the response is JSON or plain text
        if (typeof data === 'string') {
          console.log('Non-JSON response:', data);
          Swal.fire({ icon: 'error', text: `Received non-JSON response: ${data}` });
        } else if (data.success) {
          //navigate('/users');
          Swal.fire({ icon: 'success', text: 'Employees successfully assigned' });
          // Update registered users
          const newUserIds = new Set(registeredUserIds);
          const newParticipants = [...participants]; // clone to update

          selectedEmployees.forEach(id => {
            newUserIds.add(id);

            // If not already in participants, add them
            if (!participants.some(p => p.user_id === id)) {
              newParticipants.push({
                user_id: id,
                is_KI: !!selectedKIFlags[id]
              });
            }
          });

          setRegisteredUserIds(newUserIds);
          setParticipants(newParticipants);
          setSelectedEmployees([]);
          setSelectedKIFlags({});
          setSelectedEmployeeNames({});
        } else if (data.blockedByPrerequisites && data.blockedByPrerequisites.length > 0) {
          const listHtml = data.blockedByPrerequisites.map(b => {
            const name = selectedEmployeeNames[b.id] || employees.find(e => e.id === b.id)?.name;
            return `<li><strong>${name || 'Employee #' + b.id}</strong>: missing ${b.missing.join(', ')}</li>`;
          }).join('');
          Swal.fire({
            title: 'Missing prerequisites',
            html: `<p>The following employees have not completed the required prerequisite course(s):</p><ul style="text-align:left">${listHtml}</ul>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Assign anyway',
            cancelButtonText: 'Cancel',
          }).then(result => {
            if (result.isConfirmed) {
              handleAssign(true);
            }
          });
        } else if (data.blockedByCapacity && data.blockedByCapacity.length > 0) {
          // Unlike prerequisites, capacity has no bypass — the training is
          // physically full, so this is informational only.
          const listHtml = data.blockedByCapacity.map(b => {
            const name = selectedEmployeeNames[b.id] || employees.find(e => e.id === b.id)?.name;
            return `<li>${name || 'Employee #' + b.id}</li>`;
          }).join('');
          Swal.fire({
            title: 'Training is full',
            html: `<p>The following employees could not be assigned — no seats available:</p><ul style="text-align:left">${listHtml}</ul>`,
            icon: 'error',
          });
        } else if (data.blockedByScope && data.blockedByScope.length > 0) {
          // Shouldn't happen from normal use — the employee picker is
          // already scoped to who you're allowed to manage — so this is a
          // plain error rather than a bypassable prompt.
          const listHtml = data.blockedByScope.map(b => {
            const name = selectedEmployeeNames[b.id] || employees.find(e => e.id === b.id)?.name;
            return `<li>${name || 'Employee #' + b.id}</li>`;
          }).join('');
          Swal.fire({
            title: 'Not authorized',
            html: `<p>You are not authorized to assign the following employees:</p><ul style="text-align:left">${listHtml}</ul>`,
            icon: 'error',
          });
        } else if (data.blockedByStatus && data.blockedByStatus.length > 0) {
          // Shouldn't happen from normal use either — the picker already
          // only offers active employees — so no bypass, same as scope.
          const listHtml = data.blockedByStatus.map(b => {
            const name = selectedEmployeeNames[b.id] || employees.find(e => e.id === b.id)?.name;
            return `<li>${name || 'Employee #' + b.id}</li>`;
          }).join('');
          Swal.fire({
            title: 'Not active',
            html: `<p>The following employees are not active and cannot be assigned:</p><ul style="text-align:left">${listHtml}</ul>`,
            icon: 'error',
          });
        } else {
          console.error('PHP Error:', data.error); // Log PHP error to the console
          Swal.fire({ icon: 'error', text: `Failed to assign employees: ${data.error}` });
        }
      })
      .catch(error => {
        console.error('Error assigning training:', error);
        Swal.fire({ icon: 'error', text: 'Error: ' + error.message });
      });
  };

  return (
    <div className='p-2'>
      <h2>Assign Employees to Training</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {/* Display training information */}
      {training && (
        <div>
          <h4>Training Details</h4>
          <p><strong>Code:</strong> {training.code}<br/>
          <strong>Title:</strong> {training.title}<br/>
          <strong>Description:</strong> {training.description}</p>
          <p><strong>Date:</strong> {training.start_date} to {training.end_date}<br/>
          <strong>Location:</strong> {training.location}</p>
        </div>
      )}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Select</th>
            <th>Attend as KI</th>
            <th>Prerequisite Qualification</th>
            <th>Designation</th>
            <th>Company</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee, index) => {
            const isRegistered = registeredUserIds.has(employee.id);
            return (
            <tr key={employee.id}>
              <td>{index + 1}</td>
              <td>{employee.name}</td>
              {isRegistered ? (
                <td className='text-center'>
                  <i className="bi bi-person-check" style={{ color: 'green', fontSize: '1.2rem' }}></i>
                  </td>
                  ) : (
                  <td className='pl-4 text-center'>
                  <Form.Check
                    type="checkbox"
                    onChange={() => handleCheckboxChange(employee.id)}
                    checked={selectedEmployees.includes(employee.id)}
                  />
                </td>
                )}
              <td className='text-center'>
                {isRegistered ? (
                  <Form.Check
                    type="checkbox"
                    checked={!!participants?.find(p => p.user_id === employee.id)?.is_KI}
                    disabled
                  />
                ) : selectedEmployees.includes(employee.id) ? (
                  <Form.Check
                    type="checkbox"
                    checked={!!selectedKIFlags[employee.id]}
                    onChange={() => handleKICheckboxChange(employee.id)}
                  />
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
              <td className='text-center'>{qualificationBadge(employee.id)}</td>
              <td>{employee.designation}</td>
              <td>{employee.company}</td>
            </tr>
          );
        })}
        </tbody>
      </Table>
      <PaginationControls page={employeePage} totalPages={employeeTotalPages} totalRows={employeeTotalRows} onPageChange={setEmployeePage} />
      <Button variant="primary" className="mt-3" onClick={() => handleAssign()}>Assign Selected Employees ({selectedEmployees.length})</Button>
    </div>
  );
};

export default AssignTraining;
